import { EventInteractions } from '@eventbrite/ads';
import { ReportAdModal } from '@eventbrite/ads-event-card-extra';
import { HorizontalDotsChunky } from '@eventbrite/eds-iconography';
import { MoreActions } from '@eventbrite/eds-more-actions';
import {
    EventCardBadge,
    EventCardBOGOLabel,
    EventCardBookmarksCount,
    EventCardDate,
    EventCardDetails,
    EventCardDetailsSpacer,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoCode,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    getShareActionProps,
    HorizontalEventCard,
    HorizontalEventCardColumn,
    HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { Button } from '@eventbrite/marmalade';
import React, { useRef, useState } from 'react';

import { useEnvContext } from '../../context';
import { useShowLikesExperiment } from '../../pages/search/hooks';
import { defaultUtmOptions } from '../DiscoverEventCard/defaults';
import { getEventCardSignalsVisibility } from '../DiscoverEventCard/Experiments/eventCardSignalsVisibility';
import { DiscoverEventCardProps } from '../DiscoverEventCard/types';
import styles from './PromotedEventStackEventCard.module.scss';

enum PromotedActions {
    hideAd = EventInteractions.HideAd,
    reportAd = EventInteractions.ReportAd,
}

/**
 * Custom event card component specifically for PromotedEventStack.
 * Uses the same interface and design elements as DiscoverHorizontalEventCard
 * but can be modified independently without affecting the original component.
 */
export const PromotedEventStackEventCard: React.FunctionComponent<
    DiscoverEventCardProps & {
        onRemoveEvent?: (eventId: string) => void;
        getTrackingContext?: (data: any) => any;
    }
> = ({
    event,
    locale,
    isRaised,
    isLazyImage,
    statsigLocationString,
    affCode = 'ebdssbdestsearch',
    moreActions,
    isExpansionsLoading,
    statsigMetadata,
    onShare,
    shareOptions = {},
    onClick,
    isAuthenticated,
    hasFullClick,
    hasReducedSignals = false,
    hasCTA,
    showPlaceLocationInfo,
    onRemoveEvent,
    getTrackingContext,
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { user } = useEnvContext();
    const [showReportModal, setReportModalVisibility] = useState(false);

    const { utmOptions = defaultUtmOptions } = shareOptions;
    const { urgencySignals } = event;
    const containerRef = useRef<HTMLSpanElement>(null);

    const { showUrgencySignals, showDiscountCode, showBOGOLabel } =
        getEventCardSignalsVisibility({
            event,
            withLimitedVisibility: hasReducedSignals,
        });

    const linkProps = getEventCardLinkProps({
        event,
        affCode: affCode,
        onClick,
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        locations: event.locations,
        style: showPlaceLocationInfo ? 'place_and_venue' : 'venue',
    });

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    // Only show active Sales Status (sold out etc)
    // For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
        statsigMetadata,
    };

    const isShowLikesExperimentEnabled = useShowLikesExperiment(
        event.isPromoted,
    );

    const actionHandler = (action: PromotedActions) => {
        switch (action) {
            case PromotedActions.hideAd:
                onRemoveEvent?.(event.id);
                sendActionMetrics(event, PromotedActions.hideAd, containerRef);
                break;
            case PromotedActions.reportAd:
                setReportModalVisibility(true);
                break;
            default:
                break;
        }
    };

    const sendActionMetrics = (
        event: any,
        action: PromotedActions,
        element: React.RefObject<HTMLSpanElement>,
    ) => {
        const interactionEvent = new CustomEvent('event-interaction', {
            detail: {
                id: event.id,
                action,
                event,
            },
            bubbles: true,
        });

        element.current?.dispatchEvent(interactionEvent);
    };

    return (
        <section className={styles.cardWrapper}>
            <HorizontalEventCard isRaised={isRaised} actionVisibility="hidden">
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <HorizontalEventCardColumn
                        tokens={{
                            '--HorizontalCardColumnMarginRight': '24px',
                        }}
                    >
                        <EventCardLink {...linkProps}>
                            <EventCardImage
                                name={event.name}
                                loading={isLazyImage ? 'lazy' : 'eager'}
                                tokens={{
                                    ...HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
                                    '--image-background-color': event.edgeColor,
                                }}
                                src={event.imageUrl}
                            />
                        </EventCardLink>
                    </HorizontalEventCardColumn>
                    <EventCardDetails
                        space="xs"
                        tokens={{
                            '--EventCardDetailsPadding': '0 16px 0 0',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--two"
                                title={event.name}
                                color="neutral-800"
                            />
                        </EventCardLink>
                        <EventCardDate
                            className="event-card__clamp-line--one"
                            formattedDateString={formattedDateString}
                            variant="body-md"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                        />
                        <EventCardDetailsSpacer />
                        <div className={styles.priceWrapper}>
                            <EventCardPrice
                                isFree={event.isFree}
                                formattedPriceString={formattedPriceString}
                                variant="body-md-bold"
                                color="neutral-800"
                                isLoading={isExpansionsLoading}
                            />
                            {showDiscountCode ? (
                                <EventCardPromoCode
                                    openDiscount={event.openDiscount}
                                    locale={locale}
                                />
                            ) : showBOGOLabel ? (
                                <EventCardBOGOLabel
                                    specialDiscounts={event.specialDiscounts}
                                />
                            ) : !formattedSalesStatus && showUrgencySignals ? (
                                <EventCardUrgencySignals
                                    className={styles.urgencySignals}
                                    urgencySignals={urgencySignals}
                                />
                            ) : null}
                        </div>
                    </EventCardDetails>
                    <div className={styles.buttonsColumn}>
                        {isShowLikesExperimentEnabled && (
                            <EventCardBookmarksCount
                                bookmarksCount={event.bookmarksCount}
                                data-testid="bookmarks-count"
                            />
                        )}
                        <span
                            className={styles.promotedActionsContainer}
                            ref={containerRef}
                        >
                            <MoreActions
                                items={[
                                    {
                                        content: gettext('Hide this ad'),
                                        value: PromotedActions.hideAd,
                                    },
                                    {
                                        content: gettext('Report this ad'),
                                        value: PromotedActions.reportAd,
                                    },
                                ]}
                                style="dark"
                                iconType={<HorizontalDotsChunky />}
                                title={gettext('Promoted event actions')}
                                onAction={actionHandler}
                            />
                        </span>
                        <EventCardShareAction
                            {...getShareActionProps({
                                name: event.name,
                                id: event.id,
                                url: event.url,
                                affCode: affCode,
                                onClick: onShare,
                                utmOptions,
                            })}
                        />
                        <EventCardSaveAction
                            isAuthenticated={!!isAuthenticated}
                            savedByYou={event.savedByYou}
                            id={event.id}
                            eventName={event.name}
                            imageUrl={event.imageUrl}
                        />
                        {moreActions}
                    </div>
                    {showReportModal && getTrackingContext && (
                        <ReportAdModal
                            event={event}
                            handleClose={() => setReportModalVisibility(false)}
                            onSuccessClose={() => onRemoveEvent?.(event.id)}
                            getTrackingContext={getTrackingContext}
                        />
                    )}
                    {hasFullClick && (
                        <EventCardLink
                            {...linkProps}
                            className={styles.fullClickLink}
                            data-testid="event-card-full-click-link"
                        />
                    )}
                    {hasCTA && (
                        <EventCardLink
                            {...linkProps}
                            className={styles.ctaLink}
                        >
                            <Button variant="secondary">
                                {gettext('Get tickets')}
                            </Button>
                        </EventCardLink>
                    )}
                </EventCardTracking>
            </HorizontalEventCard>
        </section>
    );
};
